import { FC, useContext, useRef, useState } from "react"
import { ZipProjectContext } from "./ZipProjectContext";
import { ResourceService, ResourceStatus, ResourceType } from "@app/resources";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { RoleRestricted, Roles } from "@app/user";
import { Link, useLocation } from "react-router-dom";
import { EditProject } from "../network-assessment/EditProject";
import { isProjectRunning, onRunProject } from "./utility";
import { ZipJobsPanelContentLink } from "../network-assessment/ZipJobsPanelContentLink";

export const ZipProjectControls: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { state, dispatch } = projectContext;
  const [projectOptionsDirty, setProjectOptionsDirty] = useState(false);
  const resourceService = useRef(new ResourceService()).current;
  const location = useLocation();

  const { isArchived, isShared, name, resourceId } = state.project.resource;
  const { isReadOnly } = state.project;
  const isRunning = isProjectRunning(state.project);
  const archiveIcon = isArchived ? "fa fa-sellsy" : "fa fa-archive";
  const archiveText = isArchived ? "Unarchive" : "Archive";

  const onSubmitEditProject = () => {
    projectContext.onSubmitEditProject(state.project.resource.resourceId);
  }

  const handleArchiveConfirm = async () => {
    if (isRunning) {
      return;
    }

    if (isArchived) {
      await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Active);
    } else {
      await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Archived);
    }
    window.location.href = `${window.customUIBaseUrl}/`;
  }

  return (
    <>
      <FlyoutLink
        content={{
          body: (
            <EditProject
              sidePanelId={""}
              disabled={false}
              onChange={(dirty: boolean) => setProjectOptionsDirty(dirty)}
              projectId={resourceId}
              onRunProject={async () => onRunProject(dispatch, projectContext.onRunProject, resourceId)}
              onSubmitSuccess={async () => { onSubmitEditProject() }}
            />
          ),
          title: "Project Options",
        }}
        modalContent={{
          body: "You will lose unsaved changes. Continue with closing the sidebar?",
          cancelButtonText: "Cancel",
          confirmButtonText: "Continue",
          title: "Warning",
        }}
        size="80"
        warnOnClose={projectOptionsDirty}
        testId="zip-project-controls__project-options-link"
        onClose={() => { setProjectOptionsDirty(false) }}
      >
        <i className="fa fa-pencil-square-o" />{isShared ? " " : "Edit "}Project Options
      </FlyoutLink >
      {!isShared && (
        <>
          <span className="ml10">
            <Link
              to={{
                pathname: `/resources/share`,
                state: {
                  breadcrumbItems: [
                    ...breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment],
                    {
                      link: location.pathname,
                      text: state.project.resource.name,
                    }
                  ],
                  isSingleResource: true,
                  resourceDescription: "Project",
                  resourceId: state.project.resource.resourceId,
                  resourceTypes: [ResourceType.Project],
                },
              }}
            >
              <i className="fa fa-share" /> Share
            </Link>
          </span>
          <ModalLink
            classNames="ml10 side-panel-link"
            disabled={isRunning || (isReadOnly && !state.project.resource.isArchived)}
            content={{
              body: `${archiveText} this project?`,
              cancelButtonText: "Cancel",
              confirmButtonAction: handleArchiveConfirm,
              confirmButtonText: archiveText,
              confirmButtonType: archiveText === "Archive" ? "primary-danger" : "primary",
              title: archiveText,
            }}
            testId="zip-project-controls__archive-project-modal-link"
          >
            <i className={`${archiveIcon}`} /> {archiveText}
          </ModalLink>
          <span className="ml10">
            <Link
              to={{
                pathname: `/projects/${resourceId}/clone`,
                state: {
                  linkToProject: true,
                },
              }}
            >
              <i className="fa fa-clone" /> Clone
            </Link>
          </span>
          <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
            <span className="ml10">
              <ZipJobsPanelContentLink projectId={resourceId} projectName={name} inline={true} />
            </span>
          </RoleRestricted>
        </>
      )
      }
    </>
  );
}